import React, { useState, useEffect, useMemo } from 'react';
import { PDFDownloadLink, Page, Text, View, Document, Image, StyleSheet } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import {
  Pane,
  Heading,
  Text as EvergreenText,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Spinner,
} from 'evergreen-ui';
import logo from '../assets/Chasylmlogo.png';
import watermark from '../assets/Chasylmlogo.png';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

// -------------------------------------------------
// PDF Styles (static - moved outside component)
// -------------------------------------------------
const pdfStyles = StyleSheet.create({
  page: { padding: 30, position: 'relative', backgroundColor: '#F2EED7' },
  header: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 },
  contactInfo: { top: 20, fontSize: 10, textAlign: 'right' },
  detailsText: { fontSize: 12 },
  logo: { width: 100, height: 80, marginBottom: 10 },
  watermark: { position: 'absolute', width: '90%', height: '60%', opacity: 0.1, top: 100, left: 0 },
  table: { display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, margin: '10px 0' },
  tableRow: { margin: 'auto', flexDirection: 'row' },
  tableCol: { width: '16.66%', borderStyle: 'solid', borderWidth: 1, padding: 8 },
  tableCell: { margin: 'auto', fontSize: 10 },
  title: { fontSize: 16, textAlign: 'center', marginBottom: 10 },
  section: { marginBottom: 10 },
  qrCodeContainer: { textAlign: 'start', marginTop: 20, marginBottom: 20 },
  qrCode: { width: 100, height: 100, marginTop: 10 },
});

function PayPalReceipt() {
  const [receiptData, setReceiptData] = useState(null);
  const [booking, setBooking] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [loading, setLoading] = useState(true);

  // -------------------------------------------------
  // Load PayPal receipt and booking details
  // -------------------------------------------------
  useEffect(() => {
    const loadReceipt = async () => {
      const raw = sessionStorage.getItem('paypal_receipt');
      if (!raw) {
        setLoading(false);
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        console.error("Invalid JSON in sessionStorage");
        setLoading(false);
        return;
      }

      setReceiptData(parsed);

      const orderTrackingId = parsed.orderID || parsed.id;
      if (!orderTrackingId) {
        console.error("orderID not found in paypal_receipt");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/paypal/booking/${orderTrackingId}`);
        if (!res.ok) throw new Error("Booking not found");
        const book = await res.json();
        setBooking(book);

        // Generate Simplified QR Code (short, clean, scannable)
        const capture = parsed.purchase_units?.[0]?.payments?.captures?.[0] || {};
        const amount = capture.amount?.value || parsed.amount || "0.00";
        const currency = capture.amount?.currency_code || parsed.currency || "USD";

        const qrPayload = {
          Name: `${book.firstName || parsed.payer?.name?.given_name || 'Unknown'} ${book.lastName || parsed.payer?.name?.surname || 'User'}`,
          Destination: book.destinationName || 'N/A',
          Amount: `${amount} ${currency}`,
          Date: book.travel_date || new Date().toLocaleDateString(),
          Ref: book.merchant_reference || `PP-${Date.now()}`,
          Confirm: parsed.captureId || 'N/A',
        };

        // Short stringified JSON for QR
        const qrValue = JSON.stringify(qrPayload);

        QRCode.toDataURL(qrValue, { width: 180, margin: 1 }, (err, url) => {
          if (!err) setQrCodeImage(url);
        });
      } catch (err) {
        console.error("Failed to fetch booking:", err);
      } finally {
        setLoading(false);
      }
    };

    loadReceipt();
  }, []);

  const capture = receiptData?.purchase_units?.[0]?.payments?.captures?.[0];
  const isSuccess = receiptData?.status === 'COMPLETED';

  // -------------------------------------------------
  // Build transaction data
  // -------------------------------------------------
  const transactionData = useMemo(() => ({
    booking: {
      firstName: booking?.firstName || '',
      lastName: booking?.lastName || '',
      email: booking?.email || '',
      phone: booking?.phone || '',
      destinationName: booking?.destinationName || '',
      amount: booking?.amount || '0.00',
      currency: booking?.currency || 'USD',
      travel_date: booking?.travel_date || '',
      adult: booking?.adult || 0,
      child: booking?.child || 0,
      remainingAmount: booking?.remainingAmount || '0.00',
      merchant_reference: booking?.merchant_reference || '',
    },
    payment_method: 'PayPal',
    created_date: capture?.create_time || new Date().toISOString(),
    confirmation_code: receiptData?.captureId || 'N/A',
    payment_status_description: receiptData?.status || 'COMPLETED',
    order_tracking_id: receiptData?.orderID || receiptData?.id,
    payment_account: booking?.payment_account || receiptData?.payer?.email_address || 'N/A',
    message: booking?.message || 'Payment processed via PayPal.',
  }), [booking, receiptData, capture]);

  // -------------------------------------------------
  // Memoized PDF Document
  // -------------------------------------------------
  const MyDocument = useMemo(() => (
    <Document>
      <Page style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <Image src={logo} style={pdfStyles.logo} />
          <View style={pdfStyles.contactInfo}>
            <Text>info@chasylmtours.com</Text>
            <Text>Tel: +254720727776</Text>
            <Text>Nairobi, Kenya</Text>
          </View>
        </View>

        <Image src={watermark} style={pdfStyles.watermark} />
        <Text style={pdfStyles.title}>Chasylm Tours & Safaris Receipt</Text>

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.detailsText}>
            Name: {transactionData.booking.firstName} {transactionData.booking.lastName}
          </Text>
          <Text style={pdfStyles.detailsText}>Phone: {transactionData.booking.phone}</Text>
          <Text style={pdfStyles.detailsText}>Email: {transactionData.booking.email}</Text>
          <Text style={pdfStyles.detailsText}>Destination: {transactionData.booking.destinationName}</Text>
          <Text style={pdfStyles.detailsText}>
            Travel Date:{' '}
            {transactionData.booking.travel_date
              ? new Date(transactionData.booking.travel_date).toLocaleDateString()
              : 'N/A'}
          </Text>
        </View>

        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableRow}>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>Payment Method</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>Amount (USD)</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>Merchant Reference</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>Payment Date</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>Adult(s)</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>Child(ren)</Text></View>
          </View>
          <View style={pdfStyles.tableRow}>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>{transactionData.payment_method}</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>{transactionData.booking.amount}</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>{transactionData.booking.merchant_reference}</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>{new Date(transactionData.created_date).toLocaleDateString()}</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>{transactionData.booking.adult}</Text></View>
            <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>{transactionData.booking.child}</Text></View>
          </View>
        </View>

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.detailsText}>Tracking ID: {transactionData.order_tracking_id}</Text>
          <Text style={pdfStyles.detailsText}>Payment Account: {transactionData.payment_account}</Text>
          <Text style={pdfStyles.detailsText}>Confirmation Code: {transactionData.confirmation_code}</Text>
          <Text style={pdfStyles.detailsText}>Balance: {transactionData.booking.remainingAmount}</Text>
        </View>

        {qrCodeImage && (
          <View style={pdfStyles.qrCodeContainer}>
            <Text style={pdfStyles.detailsText}>Scan to verify receipt</Text>
            <Image src={qrCodeImage} style={pdfStyles.qrCode} />
          </View>
        )}

        <Text style={pdfStyles.detailsText}>Thank you for booking with us!</Text>
      </Page>
    </Document>
  ), [transactionData, qrCodeImage]);

  if (loading || !receiptData) {
    return (
      <Pane display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner />
        <EvergreenText marginLeft={16}>Loading receipt...</EvergreenText>
      </Pane>
    );
  }

  return (
    <Pane elevation={1} padding={24} marginX="auto" marginTop={80} marginBottom={16} maxWidth={800} background="tint2" borderRadius={8}>
      <Heading size={800} marginBottom={16}>PayPal Transaction Status</Heading>

      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Detail</TableHeaderCell>
            <TableHeaderCell>Value</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>Merchant Reference</TableCell><TableCell>{transactionData.booking.merchant_reference}</TableCell></TableRow>
          <TableRow><TableCell>Amount</TableCell><TableCell>{transactionData.booking.amount} {transactionData.booking.currency}</TableCell></TableRow>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell style={{ color: isSuccess ? 'green' : 'red', fontWeight: 'bold' }}>
              {transactionData.payment_status_description}
            </TableCell>
          </TableRow>
          <TableRow><TableCell>Payment Method</TableCell><TableCell>PayPal</TableCell></TableRow>
          <TableRow><TableCell>Transaction Date</TableCell><TableCell>{new Date(transactionData.created_date).toLocaleString()}</TableCell></TableRow>
          <TableRow><TableCell>Confirmation Code</TableCell><TableCell>{transactionData.confirmation_code}</TableCell></TableRow>
        </TableBody>
      </Table>

      <Pane marginTop={16} display="flex" justifyContent="center" gap={12}>
        <PDFDownloadLink document={MyDocument} fileName="paypal_receipt.pdf">
          {({ loading }) => (
            <Button appearance="primary" isLoading={loading}>
              {loading ? 'Preparing...' : 'Download Receipt (PDF)'}
            </Button>
          )}
        </PDFDownloadLink>
        <Button onClick={() => { sessionStorage.removeItem('paypal_receipt'); window.location.href = '/'; }}>
          Back to Home
        </Button>
      </Pane>
    </Pane>
  );
}

export default PayPalReceipt;
